import React, { useEffect, useState } from "react";
import MetricCard from "./MetricCard";
import { useMyGymMetrics } from "../../../apis/metricsApi.hooks";
import { AlertCircle } from "lucide-react";
import {
  Card,
  CardContent,
  EmptyState,
  TooltipProvider,
} from "@pushpress/pushpress-ui";
import { useAnalytics } from "../../../components/providers/AnalyticsProvider";
import { ADMIN, FRONTDESK, StaffRole } from "../../../types/roles";
import { DateRangePicker } from "../../../components/date-range-picker/date-range-picker";
import { format } from "date-fns";
import { SHOW_PEOPLE_PAGE_FEATURE_FLAG } from "../../../context/features/features.utils";
import { useExperiment } from "../../../hooks/useExperiment";

interface GymMetric {
  title: string;
  currentValue: number;
  previousValue: number;
  unit?: string;
  reportUrl?: string;
  reportMinRole?: StaffRole;
  isLegacyPage: boolean;
}

const MyGym: React.FC = () => {
  const { trackEvent } = useAnalytics();
  const { useExperimentValue } = useExperiment();
  const isPeoplePageEnabled = useExperimentValue(
    SHOW_PEOPLE_PAGE_FEATURE_FLAG,
    false,
  ) as boolean;

  const [endDate, setEndDate] = useState<Date>(new Date());
  const [startDate, setStartDate] = useState<Date>(() => {
    const date = new Date();
    date.setDate(date.getDate() - 7); // Default to 7 days ago
    return date;
  });
  const [dateRangeLabel, setDateRangeLabel] = useState<string>("Last 7 Days");

  // Format dates for API
  const formattedStartDate = format(startDate, "yyyy-MM-dd");
  const formattedEndDate = format(endDate, "yyyy-MM-dd");

  const {
    data: metricsResponse,
    isLoading,
    isError,
  } = useMyGymMetrics({
    start: formattedStartDate,
    end: formattedEndDate,
  });

  useEffect(() => {
    if (isError) {
      trackEvent("My Gym Error", {
        error: "Error fetching gym metrics",
      });
    }
  }, [isError, trackEvent]);

  // Extract metrics from the response
  const getMetricValue = (
    metricName: string,
    field: "current" | "previous",
  ) => {
    const metric = metricsResponse?.data?.find((m) => m.metric === metricName);
    return metric ? metric[field] : 0;
  };

  const metrics: GymMetric[] = [
    {
      title: "Leads",
      currentValue: getMetricValue("new_leads", "current"),
      previousValue: getMetricValue("new_leads", "previous"),
      reportUrl: isPeoplePageEnabled
        ? "/people/leads"
        : `/members/leads?start=${formattedStartDate}&end=${formattedEndDate}`,
      reportMinRole: FRONTDESK,
      unit: "members",
      isLegacyPage: true,
    },
    {
      title: "New Memberships",
      currentValue: getMetricValue("new_memberships", "current"),
      previousValue: getMetricValue("new_memberships", "previous"),
      reportUrl: "/reports/subscription_change",
      reportMinRole: ADMIN,
      unit: "members",
      isLegacyPage: true,
    },
    {
      title: "Check-ins",
      currentValue: getMetricValue("checkins", "current"),
      previousValue: getMetricValue("checkins", "previous"),
      reportUrl: "/reporting/engagement?tab=Class+Attendance",
      reportMinRole: FRONTDESK,
      unit: "members",
      isLegacyPage: false,
    },
  ];

  return (
    <TooltipProvider>
      <div className="tw-mb-6">
        <div className="tw-flex sm:tw-w-full tw-flex-col sm:tw-flex-row tw-gap-4 tw-mb-4">
          <div className="tw-flex tw-w-full sm:tw-w-fit tw-items-center tw-justify-between">
            <h2 className="tw-text-lg tw-font-bold tw-text-gray-700">
              Dashboard
            </h2>
          </div>
          <div className="tw-flex tw-w-full tw-justify-start tw-gap-2">
            <DateRangePicker
              mode="range"
              startDate={startDate}
              endDate={endDate}
              placeholder="Select a range"
              onChange={(
                value: { start: Date; end: Date },
                preset?: string,
              ) => {
                setStartDate(value.start);
                setEndDate(value.end);
                setDateRangeLabel(preset || "Custom Range");
              }}
            />
          </div>
        </div>

        {isError && (
          <Card className="tw-bg-white/75 tw-flex tw-items-center tw-justify-center">
            <CardContent className="tw-pb-0">
              <EmptyState
                description="Oops, there was an error fetching metrics"
                icon={
                  <AlertCircle className="tw-h-7 tw-w-7 tw-text-gray-500" />
                }
                secondaryActionOnClick={() => {
                  window.location.reload();
                }}
                secondaryActionLabel="Reload Page"
              />
            </CardContent>
          </Card>
        )}

        {!isError && (
          <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-4">
            {metrics.map((metric, index) => (
              <MetricCard
                key={index}
                title={metric.title}
                currentValue={metric.currentValue}
                previousValue={metric.previousValue}
                reportUrl={metric.reportUrl}
                reportMinRole={metric.reportMinRole}
                isLoading={isLoading}
                isLegacyPage={metric.isLegacyPage}
                dateRangeLabel={dateRangeLabel}
              />
            ))}
          </div>
        )}
      </div>
    </TooltipProvider>
  );
};

export default MyGym;
